import { Alert, Flex } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import Filters from "../../components/Filters";
import MainMetrics from "../../components/MainMetrics";
import { useAuth } from "../../context/AuthContext";
import useCollections from "../../hooks/useCollections";
import useEventRevenueMetrics from "../../hooks/useEventRevenueMetrics";
import useRevenueKpisV2 from "../../hooks/useRevenueKpisV2";
import { REVENUE_KPIS_METRICS } from "../../utils/constants";
import {
  mensagemDeErro,
  periodoSemiaberto,
} from "../collections-change/promoHelpers";
import CollectionsBubbleChart from "./CollectionsBubbleChart";
import EventoSkusCard from "./EventoSkusCard";
import RevenueCardChart from "./RevenueCardChart";
import {
  ESCOPO_ZONA,
  avisoDoEvento,
  dadosDosCards,
  indisponiveisDoEvento,
  kpisDoEvento,
} from "./eventoHelpers";
import { filtrosDaV2, indisponiveisDaV2 } from "./kpisDaV2";

const ESTILO_DO_AVISO = { width: "85%", maxWidth: "100%" };

const InitialDateRange = () => {
  const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
  const thirtyDaysAgo = dayjs().subtract(30, "day").format("YYYY-MM-DD");
  const date_range = [thirtyDaysAgo, yesterday];
  return date_range;
};

export default function RevenueRefact() {
  const { user } = useAuth();
  const branchId = user?.active_branch_id ?? null;
  const [selectedDateRange, setSelectedDateRange] =
    useState(InitialDateRange());
  const [selectedZones, setSelectedZones] = useState(["all"]);
  const [selectedCollections, setSelectedCollections] = useState(["all"]);
  const [selectedSellers, setSelectedSellers] = useState(["all"]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const {
    modoOuran,
    isLoading: modoCarregando,
    legacyIdDaColecao,
  } = useCollections();
  const modoResolvido = !modoCarregando;

  useEffect(() => {
    setSelectedEvent(null);
  }, [branchId]);

  const semFiltro =
    selectedZones.length === 1 &&
    selectedZones[0] === "all" &&
    selectedCollections.length === 1 &&
    selectedCollections[0] === "all" &&
    selectedSellers.length === 1 &&
    selectedSellers[0] === "all";

  const sellerActive = !(
    selectedSellers.length === 1 && selectedSellers[0] === "all"
  );

  const sellerNote =
    " As entradas e o tempo de atenção são dados da loja completa e não são filtrados por vendedor.";

  const revenueKpis = REVENUE_KPIS_METRICS.filter(
    (kpi) => kpi.key !== "percent_vendas_sem_zona" || semFiltro
  ).map((kpi) => {
    if (kpi.key === "faturamento_por_tempo_permanencia" && sellerActive) {
      return {
        ...kpi,
        description: kpi.description + sellerNote,
      };
    }
    return kpi;
  });

  const comEvento = selectedEvent !== null;
  const periodo = periodoSemiaberto(selectedDateRange);

  const filtrosDaTela = filtrosDaV2({
    zonas: selectedZones,
    colecoes: selectedCollections,
    vendedores: selectedSellers,
    modoOuran,
    legacyIdDaColecao,
  });

  const kpisDaLoja = useRevenueKpisV2({
    branchId,
    start: periodo?.start,
    end: periodo?.end,
    filtros: filtrosDaTela,
    enabled: !comEvento && modoResolvido,
  });

  const faturamentoDoEvento = useEventRevenueMetrics({
    eventId: selectedEvent,
    start: periodo?.start,
    end: periodo?.end,
    enabled: comEvento,
  });
  const evento = faturamentoDoEvento.data?.meta?.event ?? null;
  const escopo = evento?.escopo ?? null;
  const porZona = escopo === ESCOPO_ZONA;
  const comSkus = comEvento && !!evento?.periodo_efetivo && !porZona;

  const skusDoEvento = useEventRevenueMetrics({
    eventId: selectedEvent,
    start: periodo?.start,
    end: periodo?.end,
    groupBy: "product",
    enabled: comSkus,
  });

  const periodoDaZona = evento?.periodo_efetivo;
  const zonasDoEvento = evento?.zonas ?? [];
  const { data: kpisDaZona, isLoading: isLoadingKpisDaZona } = useRevenueKpisV2(
    {
      branchId,
      start: periodoDaZona?.start,
      end: periodoDaZona?.end,
      filtros: { zone_id: zonasDoEvento },
      enabled: comEvento && porZona && zonasDoEvento.length > 0,
    }
  );

  const kpisDaTela = comEvento
    ? kpisDoEvento(REVENUE_KPIS_METRICS, escopo)
    : revenueKpis;
  const chavesDaTela = kpisDaTela.map((kpi) => kpi.key);
  const dadosDoEvento = comEvento
    ? dadosDosCards({
        totais: faturamentoDoEvento.data?.totals,
        kpisDaZona,
        escopo,
      })
    : null;
  const indisponiveis = !comEvento
    ? indisponiveisDaV2(kpisDaLoja.data)
    : faturamentoDoEvento.isError
      ? chavesDaTela
      : indisponiveisDoEvento({
          dados: dadosDoEvento,
          escopo,
          chaves: chavesDaTela,
        });
  const aviso = comEvento ? avisoDoEvento(evento) : null;

  return (
    <Flex
      vertical
      gap={50}
      justify="center"
      align="center"
      style={{ width: "100%", margin: "0 auto" }}>
      <Filters
        page="revenue"
        dateRange={true}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
        selectedZones={selectedZones}
        setSelectedZones={setSelectedZones}
        selectedCollections={selectedCollections}
        setSelectedCollections={setSelectedCollections}
        selectedSellers={selectedSellers}
        setSelectedSellers={setSelectedSellers}
        selectedEvent={selectedEvent}
        setSelectedEvent={setSelectedEvent}
        filters={["data", "eventos", "zonas", "colecoes", "vendedores"]}
      />
      {comEvento && faturamentoDoEvento.isError && (
        <Alert
          type="error"
          showIcon
          style={ESTILO_DO_AVISO}
          message={mensagemDeErro(
            faturamentoDoEvento.error,
            "Não foi possível carregar o faturamento do evento."
          )}
        />
      )}
      {aviso && (
        <Alert
          type={aviso.type}
          showIcon
          style={ESTILO_DO_AVISO}
          message={aviso.message}
          description={aviso.description}
        />
      )}
      {!comEvento && kpisDaLoja.isError && (
        <Alert
          type="error"
          showIcon
          style={ESTILO_DO_AVISO}
          message={mensagemDeErro(
            kpisDaLoja.error,
            "Não foi possível carregar as métricas da loja."
          )}
        />
      )}
      {!comEvento && kpisDaLoja.produtosSemCategoria.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={ESTILO_DO_AVISO}
          message="Alguns produtos não entraram no cálculo"
          description={`${kpisDaLoja.produtosSemCategoria.length} produto(s) ainda sem categoria definida foram ignorados neste período.`}
        />
      )}
      <MainMetrics
        data={comEvento ? dadosDoEvento : kpisDaLoja.data}
        isLoading={
          comEvento
            ? faturamentoDoEvento.isLoading || isLoadingKpisDaZona
            : kpisDaLoja.isLoading
        }
        kpis={kpisDaTela}
        unavailableKeys={indisponiveis}
      />
      {comSkus && (
        <EventoSkusCard
          grupos={skusDoEvento.data?.groups}
          isLoading={skusDoEvento.isLoading}
        />
      )}
      <CollectionsBubbleChart
        selectedDateRange={selectedDateRange}
        selectedZones={selectedZones}
        selectedCollections={selectedCollections}
        selectedSellers={selectedSellers}
        modoResolvido={modoResolvido}
        branchId={branchId}
        start={periodo?.start}
        end={periodo?.end}
        filtros={filtrosDaTela}
        modoOuran={modoOuran}
      />
      <RevenueCardChart
        branchId={branchId}
        start={periodo?.start}
        end={periodo?.end}
        filtros={filtrosDaTela}
        enabled={modoResolvido}
      />
    </Flex>
  );
}
